import React from 'react';

import filesize from 'filesize';
import ClockIcon from 'icons/clock.svg';
import { useTranslation } from 'react-i18next';

import ActionButton from 'components/buttons/ActionButton';
import ActionLink from 'components/buttons/ActionLink';
import PdfDownloadContainer from 'components/messages/PdfDownloadContainer';

import { asFormattedDateTime, formatDate } from 'components/helpers/dates';

export default function DownloadContainer({
  companyBranding,
  companyInfo,
  messageDetails,
  projectInfo,
}) {
  const { name: companyName, singularRAMSName } = companyInfo;

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'messages.DownloadContainer',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const versionPdf = messageDetails.included.find(
    (i) => i.type === 'versionPdf',
  );

  const trainingReport = messageDetails.included.find(
    (i) => i.type === 'trainingReport',
  );

  const { buttonBgColour, buttonTextColour, textLinkColour } =
    companyBranding.data.attributes;

  const BrandedLink = withBranding(ActionLink, {
    color: textLinkColour,
  });

  const BrandedButton = withBranding(ActionButton, {
    backgroundColor: buttonBgColour,
    color: buttonTextColour,
  });

  const downloadAll = () =>
    window.location.assign(
      `/messages/${messageDetails.data.attributes.hashid}.zip`,
    );

  const containerProps = (resource) => ({
    createdAt: asFormattedDateTime({ date: resource.attributes.createdAt }),
    fileSize: filesize(resource.attributes.fileSize || 0, { round: 0 }),
    viewHref: resource.links.content,
    BrandedLink,
  });

  const { expiryDate, hashid } = messageDetails.data.attributes;
  const { token } = messageDetails.data.meta;
  const { name: projectName, versionNumber } = projectInfo;

  return (
    <div className='download-container tw-bg-white'>
      <div className='download-container__title tw-font-medium'>
        {companyName}
      </div>
      <div className='download-container__name tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
        {projectName}
      </div>
      <div className='download-container__sub-text tw-mb-6 tw-mt-5 tw-text-grey-600'>
        <ClockIcon
          className='download-container__svg tw-mb-px tw-mr-0.5 [&_path]:tw-fill-grey-600'
          height={18}
          width={18}
        />
        {i18nT('expiresOn', {
          expiryDate: formatDate({ date: expiryDate, format: 'd MMM yyyy' }),
          interpolation: { escapeValue: false },
        })}
      </div>
      {versionPdf && (
        <PdfDownloadContainer
          downloadHref={`/messages/${hashid}/version_pdf?token=${token}`}
          title={
            versionNumber === undefined ? singularRAMSName : (
              i18nTCommon('projects.projectVersionNumber', {
                interpolation: { escapeValue: false },
                singularRAMSName,
                versionNumber,
              })
            )
          }
          {...containerProps(versionPdf)}
        />
      )}
      {trainingReport && (
        <PdfDownloadContainer
          downloadHref={`/messages/${hashid}/training_report?token=${token}`}
          title={i18nTCommon('training.trainingReport')}
          {...containerProps(trainingReport)}
        />
      )}
      {versionPdf && trainingReport && (
        <div className='tw-mt-6'>
          <BrandedButton
            buttonText={i18nTCommon('shared.downloadAll')}
            onClick={downloadAll}
          />
        </div>
      )}
    </div>
  );
}

export const withBranding = function (Component, brandingStyle) {
  return function (props) {
    return <Component {...props} style={brandingStyle} />;
  };
};
